import React, { useState, useEffect } from 'react';
import { amDesignColors, amDesignTypography, amDesignComponents } from '../../themes/amDesignSystem';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Container,
  Paper,
  Stack,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  Tabs,
  Tab,
  Badge,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Fab,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  LinearProgress,
  Rating,
  Snackbar
} from '@mui/material';
import {
  AutoAwesome as AIIcon,
  TrendingUp as ScoreIcon,
  PostAdd as PostIcon,
  Schedule as ScheduleIcon,
  CheckCircle as PublishedIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ContentCopy as CopyIcon,
  Send as PublishIcon,
  Visibility as PreviewIcon,
  CalendarMonth as CalendarIcon,
  FilterList as FilterIcon,
  Analytics as AnalyticsIcon,
  Bookmark as TemplateIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  MoreVert as MoreIcon,
  AccessTime as TimeIcon,
  TrendingUp as TrendIcon,
  Share as ShareIcon,
  ThumbUp as LikeIcon,
  Comment as CommentIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Sort as SortIcon,
  Settings as SettingsIcon,
  Close as CloseIcon,
  Photo as PhotoIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useBusiness } from '../../contexts/BusinessContext';
import { format, addDays, isToday, isTomorrow, isPast, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import PostsWizard from './PostsWizard';
import MediaManager from '../media/MediaManager';
import LibraryPage from '../library/LibraryPage';

const PostsManager = () => {
  const { locationId } = useParams();
  const navigate = useNavigate();
  const { canUsePremiumFeatures, user, isReadOnly } = useAuth();
  const { businesses, selectedBusiness, setSelectedBusiness } = useBusiness();

  const [topLevelTab, setTopLevelTab] = useState(0); // 0 = Posts, 1 = Media, 2 = Library
  const [activeTab, setActiveTab] = useState(0);
  const [posts, setPosts] = useState({
    suggestions: [],
    scheduled: [],
    published: [],
    drafts: []
  });
  const [keywords, setKeywords] = useState([]);
  const [regionalKeywords, setRegionalKeywords] = useState([]); // Lazy loaded only when PostWizard opens
  const [rankingData, setRankingData] = useState(null);
  const [standardPlans, setStandardPlans] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [loadingWizard, setLoadingWizard] = useState(false);
  const [selectedPosts, setSelectedPosts] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created');
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'list'
  const [searchTerm, setSearchTerm] = useState('');
  const [publishedPostsError, setPublishedPostsError] = useState(null);
  const [keywordCoverage, setKeywordCoverage] = useState({
    primary: 0,
    regional: 0,
    longTail: 0,
    total: 0
  });

  // Dialog states
  const [scheduleDialog, setScheduleDialog] = useState({ open: false, post: null });
  const [editDialog, setEditDialog] = useState({ open: false, post: null });
  const [templateDialog, setTemplateDialog] = useState({ open: false });
  const [analyticsDialog, setAnalyticsDialog] = useState({ open: false });
  const [standardPlanDialog, setStandardPlanDialog] = useState({ open: false });
  const [keywordPlannerDialog, setKeywordPlannerDialog] = useState({ open: false });
  const [wizardDialog, setWizardDialog] = useState({ open: false });
  const [wizardCompleting, setWizardCompleting] = useState(false);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, post: null });
  const [viewPostDialog, setViewPostDialog] = useState({ open: false, post: null });
  const [aiInstructionsDialog, setAiInstructionsDialog] = useState({ open: false });

  // AI Customization
  const [customAiPrompt, setCustomAiPrompt] = useState('');
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // Analytics data
  const [analytics, setAnalytics] = useState({
    totalPosts: 0,
    avgEngagement: 0,
    bestPerformingPost: null,
    upcomingPosts: 0,
    totalReach: 0,
    keywordRankings: {},
    regionalCoverage: 0,
    seoScore: 0,
    publishedAnalytics: {}
  });

  useEffect(() => {
    if (locationId) {
      loadPostsData();
      loadKeywords();
      // loadRegionalKeywords(); // Removed: Backend generates own regional keywords
      loadStandardPlans();
      loadTemplates();
      calculateKeywordCoverage();
    }
  }, [locationId]);

  useEffect(() => {
    // Auto-select business if it matches the locationId
    if (businesses.length > 0 && locationId && !selectedBusiness) {
      const matchingBusiness = businesses.find(b => b.locationId === locationId || b.id === locationId);
      if (matchingBusiness) {
        setSelectedBusiness(matchingBusiness);
      }
    }
  }, [businesses, locationId, selectedBusiness, setSelectedBusiness]);

  // 🚀 NEW: React to business changes from dropdown (when selectedBusiness changes but URL might not have changed yet)
  useEffect(() => {
    if (selectedBusiness && locationId) {
      const businessLocationId = selectedBusiness.locationId || selectedBusiness.id;
      
      // If business changed, the URL should have been updated by BusinessSearchContext navigation
      // This is a safety check in case the component needs to reload data
      if (businessLocationId === locationId) {
        console.log('🔄 Business context updated, ensuring data is fresh:', {
          businessName: selectedBusiness.businessName,
          locationId: businessLocationId
        });
        
        // Optionally reload data if needed (currently data loads via locationId useEffect)
        // This could be enhanced to force refresh data here if required
      }
    }
  }, [selectedBusiness, locationId]);

  // Load custom AI prompt when business changes - fetch from DB if not in selectedBusiness
  useEffect(() => {
    if (selectedBusiness) {
      // Check if customAiPrompt is already in selectedBusiness
      if (selectedBusiness?.customAiPrompt !== undefined) {
        setCustomAiPrompt(selectedBusiness.customAiPrompt || '');
        console.log('✅ Custom AI prompt loaded from selectedBusiness:', selectedBusiness.customAiPrompt?.substring(0, 50) || '(empty)');
      } else {
        // Fetch from DB if not available
        const fetchCustomPrompt = async () => {
          try {
            const response = await fetch(
              `/api/business/${selectedBusiness.locationId || selectedBusiness.id}/custom-ai-prompt`,
              {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
              }
            );
            if (response.ok) {
              const responseData = await response.json();
              const customPrompt = responseData.data?.customAiPrompt || '';
              setCustomAiPrompt(customPrompt);
              console.log('✅ Loaded custom AI prompt from DB:', customPrompt?.substring(0, 50) || '(empty)');
            } else {
              console.warn('⚠️ Failed to fetch custom prompt:', response.status);
              setCustomAiPrompt('');
            }
          } catch (error) {
            console.warn('⚠️ Failed to fetch custom AI prompt:', error);
            setCustomAiPrompt('');
          }
        };
        fetchCustomPrompt();
      }
    }
  }, [selectedBusiness]);

  const loadPublishedPosts = async () => {
    try {
      console.log('🔄 Loading published posts from Google My Business...');
      setPublishedPostsError(null); // Clear previous errors
      
      const response = await fetch(`/api/businesses/${locationId}/published-posts`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });

      if (response.ok) {
        const publishedData = await response.json();
        if (publishedData.success && publishedData.posts) {
          console.log('✅ Loaded published posts from Google My Business:', publishedData.posts.length);
          
          // Update only published posts
          setPosts(prev => ({
            ...prev,
            published: publishedData.posts
          }));
          
          // Update analytics with published posts data
          setAnalytics(prev => ({
            ...prev,
            publishedAnalytics: publishedData.analytics,
            seoScore: publishedData.analytics?.averageCharacterCount ? Math.min((publishedData.analytics.averageCharacterCount / 800) * 100, 100) : 0,
            totalPosts: prev.totalPosts - (prev.published?.length || 0) + publishedData.posts.length
          }));
          
          return publishedData.posts;
        } else {
          throw new Error(publishedData.error || 'No posts returned from API');
        }
      } else {
        console.warn('❌ Failed to load published posts from Google My Business');
        const errorData = await response.json().catch(() => ({}));
        console.warn('Published posts error:', errorData);
        
        // Zeige verfügbare Locations für Debug-Zwecke
        if (errorData.availableLocations && errorData.availableLocations.length > 0) {
          console.log('🔍 VERFÜGBARE LOCATIONS:');
          errorData.availableLocations.forEach((loc, i) => {
            console.log(`${i+1}. Location ID: ${loc.locationId}`);
            console.log(`   Name: ${loc.locationName || loc.title}`);
            console.log(`   Account: ${loc.accountId} (${loc.accountName})`);
            console.log(`   Adresse: ${loc.address ? JSON.stringify(loc.address) : 'N/A'}`);
            console.log(`   ---`);
          });
          
          // Prüfe ob wir die erste verfügbare Location verwenden können
          const firstLocation = errorData.availableLocations[0];
          if (firstLocation && firstLocation.locationId) {
            console.log(`💡 TIPP: Die verfügbare Location-ID ist: ${firstLocation.locationId}`);
            console.log(`💡 Aktuelle URL Location-ID: ${locationId}`);
            console.log(`💡 Stimmen diese überein? ${firstLocation.locationId === locationId ? 'JA ✅' : 'NEIN ❌'}`);
            
            if (firstLocation.locationId !== locationId) {
              console.log(`🚨 PROBLEM GEFUNDEN: Die URL enthält eine falsche Location-ID!`);
              console.log(`🔧 LÖSUNG: Ändere die URL von .../${locationId}/... zu .../${firstLocation.locationId}/...`);
              
              // Speichere die korrekte Location-ID für den User
              localStorage.setItem('correct_location_id', firstLocation.locationId);
              console.log(`💾 Korrekte Location-ID gespeichert: ${firstLocation.locationId}`);
              
              // Auto-redirect nach kurzer Verzögerung
              console.log(`🔄 Leite in 3 Sekunden zur korrekten Location weiter...`);
              setTimeout(() => {
                const currentPath = window.location.pathname;
                const newPath = currentPath.replace(`/${locationId}/`, `/${firstLocation.locationId}/`);
                console.log(`🔄 Wechsle von ${currentPath} zu ${newPath}`);
                navigate(newPath, { replace: true });
              }, 3000);
            }
          }
        }
        
        const errorMessage = errorData.error || `HTTP ${response.status}: Failed to load published posts`;
        setPublishedPostsError(errorMessage);
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error loading published posts:', error);
      setPublishedPostsError(error.message);
      throw error;
    }
  };

  const loadPostsData = async () => {
    setLoading(true);
    try {
      // Load posts from multiple endpoints
      const [postsResponse, publishedResponse] = await Promise.all([
        // Load local posts (suggestions, scheduled, drafts)
        fetch(`/api/businesses/${locationId}/posts`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        }),
        // Load published posts from Google My Business
        loadPublishedPosts().catch(error => {
          console.warn('Published posts failed during initial load:', error);
          return [];
        })
      ]);

      let postsData = {
        suggestions: [],
        scheduled: [],
        published: Array.isArray(publishedResponse) ? publishedResponse : [],
        drafts: []
      };

      // Handle local posts response
      if (postsResponse.ok) {
        const data = await postsResponse.json();
        if (data.success && data.posts) {
          postsData = {
            ...postsData,
            suggestions: data.posts.suggestions || [],
            scheduled: data.posts.scheduled || [],
            drafts: data.posts.drafts || []
          };
        }
      }

      setPosts(postsData);
      console.log('📊 POSTS LOADED:', {
        locationId,
        suggestions: postsData.suggestions.length,
        scheduled: postsData.scheduled.length, 
        published: postsData.published.length,
        drafts: postsData.drafts.length
      });
      
      // Debug scheduled posts specifically
      if (postsData.scheduled.length > 0) {
        console.log('🗓️ SCHEDULED POSTS FOUND:', postsData.scheduled.map(p => ({
          id: p.id,
          status: p.status,
          scheduledFor: p.scheduledFor,
          content: p.content?.substring(0, 50) + '...',
          mediaUrl: p.mediaUrl || 'NO_MEDIA'
        })));
      } else {
        console.log('❌ NO SCHEDULED POSTS - Check business selection and location ID');
      }
      
      
      // Calculate analytics
      const totalPosts = Object.values(postsData).flat().length;
      setAnalytics(prev => ({
        ...prev,
        totalPosts,
        upcomingPosts: (postsData.scheduled || []).length,
        avgEngagement: prev.publishedAnalytics?.totalEngagement || 0
      }));

    } catch (error) {
      console.error('Error loading posts:', error);
      // Initialize empty state on error
      setPosts({
        suggestions: [],
        scheduled: [],
        published: [],
        drafts: []
      });
      setAnalytics(prev => ({
        ...prev,
        totalPosts: 0,
        upcomingPosts: 0,
        avgEngagement: 0
      }));
    }
    setLoading(false);
  };

  const loadRankingData = async () => {
    try {
      const response = await fetch(`/api/businesses/${locationId}/ranking-overview`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setRankingData(data.data);
          return data.data;
        }
      }
    } catch (error) {
      console.error('Error loading ranking data:', error);
    }
    return null;
  };

  const prioritizeKeywords = (generatedKeywords, rankingOverview) => {
    if (!rankingOverview || !rankingOverview.trackedKeywords) {
      return generatedKeywords.filter(k => k && k.trim());
    }

    const trackedKeywords = rankingOverview.trackedKeywords;
    const trackedKeywordStrings = trackedKeywords.map(k => k.keyword);
    
    // Priorisierung: Ranking-Keywords nach Performance sortieren
    const goodRankingKeywords = trackedKeywords
      .filter(k => k.averageRank <= 3)
      .sort((a, b) => a.averageRank - b.averageRank)
      .map(k => k.keyword)
      .filter(k => k && k.trim());
    
    const mediumRankingKeywords = trackedKeywords
      .filter(k => k.averageRank > 3 && k.averageRank <= 10)
      .sort((a, b) => a.averageRank - b.averageRank)
      .map(k => k.keyword)
      .filter(k => k && k.trim());
    
    const poorRankingKeywords = trackedKeywords
      .filter(k => k.averageRank > 10)
      .sort((a, b) => a.averageRank - b.averageRank)
      .map(k => k.keyword)
      .filter(k => k && k.trim());
    
    // Generierte Keywords, die nicht getrackt werden
    const otherKeywords = generatedKeywords
      .filter(k => k && k.trim() && !trackedKeywordStrings.includes(k));
    
    // Alles zusammenfügen: Beste Rankings zuerst
    return [
      ...goodRankingKeywords,
      ...mediumRankingKeywords,
      ...poorRankingKeywords,
      ...otherKeywords
    ];
  };

  const loadKeywords = async () => {
    try {
      // Erst Ranking-Daten laden
      const rankingOverview = await loadRankingData();
      
      // Dann Keyword-Suggestions laden (AI-generierte Vorschläge basierend auf Business-Daten)
      const response = await fetch(`/api/businesses/${locationId}/keyword-suggestions`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data && data.data.suggestions) {
          console.log('🎯 Raw keyword suggestions response:', data.data.suggestions);
          
          // Extract keywords from the structured suggestions
          let keywordList = [];
          
          const suggestions = data.data.suggestions;
          
          // Extract from all suggestion categories
          if (suggestions.currentKeywords && Array.isArray(suggestions.currentKeywords)) {
            keywordList.push(...suggestions.currentKeywords);
          }
          if (suggestions.businessSuggestions && Array.isArray(suggestions.businessSuggestions)) {
            keywordList.push(...suggestions.businessSuggestions);
          }
          if (suggestions.locationSuggestions && Array.isArray(suggestions.locationSuggestions)) {
            keywordList.push(...suggestions.locationSuggestions);
          }
          if (suggestions.categorySuggestions && Array.isArray(suggestions.categorySuggestions)) {
            keywordList.push(...suggestions.categorySuggestions);
          }
          
          // Fallback: try old structure for backwards compatibility
          if (keywordList.length === 0 && data.suggestions && Array.isArray(data.suggestions)) {
            keywordList = data.suggestions;
          }
          
          // Remove duplicates and filter out empty/invalid keywords
          const uniqueKeywords = [...new Set(keywordList)]
            .filter(keyword => keyword && typeof keyword === 'string' && keyword.trim().length > 0)
            .map(keyword => keyword.trim());
          
          console.log('🎯 Processed keyword suggestions:', uniqueKeywords.length, 'unique keywords:', uniqueKeywords);
          
          // Keywords mit Ranking-Daten kombinieren und priorisieren
          const prioritizedKeywords = prioritizeKeywords(uniqueKeywords, rankingOverview);
          setKeywords(prioritizedKeywords);
        }
      } else {
        console.error('❌ Failed to load keyword suggestions, HTTP status:', response.status);
        const errorData = await response.text();
        console.error('❌ Error response:', errorData);
      }
    } catch (error) {
      console.error('❌ Error loading keyword suggestions:', error);
    }
  };

  // Lazy load regional keywords only when PostWizard opens
  const loadRegionalKeywords = async () => {
    try {
      console.log('🌍 Loading regional keywords for PostWizard...');
      const response = await fetch(`/api/businesses/${locationId}/regional-keywords`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.regionalKeywords) {
          setRegionalKeywords(data.regionalKeywords);
          console.log('✅ Regional keywords loaded:', data.regionalKeywords.length);
        } else {
          setRegionalKeywords([]);
        }
      } else {
        setRegionalKeywords([]);
      }
    } catch (error) {
      console.error('Error loading regional keywords:', error);
      setRegionalKeywords([]);
    }
  };

  const loadStandardPlans = async () => {
    try {
      const response = await fetch('/api/posts/standard-plans', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.plans) {
          setStandardPlans(data.plans);
        } else {
          setStandardPlans([]);
        }
      } else {
        setStandardPlans([]);
      }
    } catch (error) {
      console.error('Error loading standard plans:', error);
      setStandardPlans([]);
    }
  };

  const calculateKeywordCoverage = () => {
    const allPosts = getAllPosts();
    const usedKeywords = new Set();
    
    allPosts.forEach(post => {
      if (post.targetKeywords) {
        post.targetKeywords.forEach(keyword => usedKeywords.add(keyword));
      }
    });

    const primaryCovered = keywords.filter(k => usedKeywords.has(k)).length;
    const regionalCovered = regionalKeywords.filter(k => usedKeywords.has(k)).length;
    const totalKeywords = keywords.length + regionalKeywords.length;
    
    setKeywordCoverage({
      primary: keywords.length > 0 ? (primaryCovered / keywords.length) * 100 : 0,
      regional: regionalKeywords.length > 0 ? (regionalCovered / regionalKeywords.length) * 100 : 0,
      longTail: usedKeywords.size > 0 ? Math.min((usedKeywords.size / Math.max(totalKeywords, 1)) * 150, 100) : 0,
      total: totalKeywords > 0 ? (usedKeywords.size / totalKeywords) * 100 : 0
    });
  };

  const loadTemplates = async () => {
    try {
      const response = await fetch(`/api/businesses/${locationId}/post-templates`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.templates) {
          setTemplates(data.templates);
        } else {
          setTemplates([]);
        }
      } else {
        setTemplates([]);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      setTemplates([]);
    }
  };

  // Save custom AI prompt
  const saveCustomAiPrompt = async () => {
    if (!selectedBusiness?.locationId) {
      console.error('No business selected');
      return;
    }

    setSavingPrompt(true);
    try {
      const response = await fetch(`/api/business/${selectedBusiness.locationId}/custom-ai-prompt`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ customAiPrompt })
      });

      if (!response.ok) {
        throw new Error('Failed to save custom AI prompt');
      }

      console.log('✅ Custom AI prompt saved successfully');
      // Update selectedBusiness in context
      if (setSelectedBusiness) {
        setSelectedBusiness({ ...selectedBusiness, customAiPrompt });
      }
      // Show success toast
      setToastMessage('✅ KI-Anweisungen erfolgreich gespeichert');
      setToastOpen(true);
    } catch (error) {
      console.error('❌ Error saving custom AI prompt:', error);
      setToastMessage('❌ Fehler beim Speichern der KI-Anweisungen');
      setToastOpen(true);
    } finally {
      setSavingPrompt(false);
    }
  };

  // Function to save posts to database
  const savePostsToDatabase = async (posts) => {
    try {
      console.log('💾 Saving posts to database:', posts.length, 'posts');
      console.log('📋 Posts with media URLs:');
      posts.forEach((post, idx) => {
        console.log(`  ${idx + 1}. Post ${post.id}: mediaUrl = ${post.mediaUrl || 'NONE'}`);
      });
      const response = await fetch(`/api/businesses/${locationId}/posts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ posts })
      });
      
      if (response.ok) {
        console.log('✅ Posts saved to database successfully');
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('❌ Failed to save posts to database:', response.status, errorData);
      }
    } catch (error) {
      console.error('Error saving posts:', error);
    }
  };

  const generateAIPosts = async (planType = 'single') => {
    if (!canUsePremiumFeatures()) {
      alert('Premium-Feature erforderlich für KI-Generierung');
      return;
    }
    
    setGenerating(true);
    try {
      const payload = {
        category: 'local_seo_focused',
        keywords: keywords,
        regionalKeywords: regionalKeywords,
        businessData: {
          id: selectedBusiness.id,
          locationId: selectedBusiness.locationId,
          businessName: selectedBusiness.businessName,
          description: selectedBusiness.description,
          categories: selectedBusiness.categories,
          address: selectedBusiness.address,
          phoneNumber: selectedBusiness.phoneNumber,
          websiteUrl: selectedBusiness.websiteUrl,
          lat: selectedBusiness.lat,
          lng: selectedBusiness.lng
        },
        count: planType === 'single' ? 5 : 10,
        focusType: 'regional_dominance',
        includeLocationVariations: true,
        seoOptimized: true
      };

      const response = await fetch(`/api/businesses/${locationId}/posts/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Posts generation response:', data);
        
        if (data.success && data.posts && data.posts.length > 0) {
          const newSuggestions = data.posts.map(post => ({
            ...post,
            status: 'suggestion',
            createdAt: new Date().toISOString(),
            author: 'Local SEO AI'
          }));
          setPosts(prev => ({
            ...prev,
            suggestions: [...prev.suggestions, ...newSuggestions]
          }));
          
          // Save generated posts to database
          await savePostsToDatabase(newSuggestions);
          
          // Recalculate keyword coverage after generating posts
          setTimeout(calculateKeywordCoverage, 500);
          
          alert(`✅ ${data.posts.length} Posts erfolgreich generiert!`);
        } else {
          console.error('❌ No posts in response:', data);
          alert('❌ Keine Posts generiert. Bitte versuchen Sie es erneut.');
        }
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('❌ Failed to generate posts:', response.status, errorData);
        alert(`❌ Fehler beim Generieren der Posts: ${errorData.error || response.statusText}`);
      }
    } catch (error) {
      console.error('❌ Error generating posts:', error);
      alert(`❌ Netzwerkfehler beim Generieren der Posts: ${error.message}`);
    }
    setGenerating(false);
  };

  const executeStandardPlan = async (plan) => {
    if (!canUsePremiumFeatures()) {
      alert('Premium-Feature erforderlich für Standardpläne');
      return;
    }

    setGenerating(true);
    try {
      const payload = {
        standardPlan: plan,
        keywords: keywords,
        regionalKeywords: regionalKeywords,
        businessData: {
          id: selectedBusiness.id,
          locationId: selectedBusiness.locationId,
          businessName: selectedBusiness.businessName,
          description: selectedBusiness.description,
          categories: selectedBusiness.categories,
          address: selectedBusiness.address,
          phoneNumber: selectedBusiness.phoneNumber,
          websiteUrl: selectedBusiness.websiteUrl,
          lat: selectedBusiness.lat,
          lng: selectedBusiness.lng
        },
        focusType: plan.keywordFocus,
        duration: plan.duration,
        postsCount: plan.postsCount,
        categories: plan.categories
      };

      const response = await fetch(`/api/businesses/${locationId}/posts/generate-plan`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.posts) {
          const scheduledPosts = data.posts.map((post, index) => ({
            ...post,
            status: 'scheduled',
            scheduledFor: addDays(new Date(), index * 2 + 1).toISOString(), // Every 2 days
            createdAt: new Date().toISOString(),
            author: 'SEO Plan Generator',
            planId: plan.id
          }));
          
          setPosts(prev => ({
            ...prev,
            scheduled: [...prev.scheduled, ...scheduledPosts]
          }));
          
          // Save scheduled posts to database
          await savePostsToDatabase(scheduledPosts);
          
          setTimeout(calculateKeywordCoverage, 500);
          setStandardPlanDialog({ open: false });
        }
      }
    } catch (error) {
      console.error('Error executing standard plan:', error);
    }
    setGenerating(false);
  };

  const handlePostAction = (action, post) => {
    // Block viewers from modifying content
    if (isReadOnly() && ['schedule', 'publish', 'edit', 'delete'].includes(action)) {
      alert('Sie haben keine Berechtigung, Posts zu ändern');
      return;
    }

    switch (action) {
      case 'schedule':
        setScheduleDialog({ open: true, post });
        break;
      case 'publish':
        publishPost(post);
        break;
      case 'edit':
        setEditDialog({ open: true, post });
        break;
      case 'delete':
        setDeleteConfirmDialog({ open: true, post });
        break;
      case 'copy':
        navigator.clipboard.writeText(post.content || post.summary);
        break;
      case 'template':
        createTemplate(post);
        break;
      case 'view':
        setViewPostDialog({ open: true, post });
        break;
      default:
        break;
    }
  };

  const publishPost = async (post) => {
    try {
      // Call backend API to publish to Google My Business
      const response = await fetch(`/api/businesses/${locationId}/posts/${post.id}/publish`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Post published successfully:', result);
        
        const publishedPost = {
          ...post,
          status: 'published',
          publishedAt: new Date().toISOString(),
          googlePostId: result.googlePostId,
          likes: 0,
          comments: 0,
          shares: 0
        };

        setPosts(prev => ({
          ...prev,
          suggestions: prev.suggestions.filter(p => p.id !== post.id),
          scheduled: prev.scheduled.filter(p => p.id !== post.id),
          published: [...prev.published, publishedPost]
        }));
      } else {
        console.error('❌ Failed to publish post');
        alert('Fehler beim Veröffentlichen des Posts');
      }
    } catch (error) {
      console.error('❌ Error publishing post:', error);
      alert('Fehler beim Veröffentlichen des Posts');
    }
  };

  const schedulePost = async (post, date) => {
    try {
      const scheduledPost = {
        ...post,
        status: 'scheduled',
        scheduledFor: date.toISOString(),
        scheduledAt: new Date().toISOString()
      };

      // Save to database
      await savePostsToDatabase([scheduledPost]);

      setPosts(prev => ({
        ...prev,
        suggestions: prev.suggestions.filter(p => p.id !== post.id),
        scheduled: [...prev.scheduled, scheduledPost]
      }));
    } catch (error) {
      console.error('❌ Error scheduling post:', error);
      alert('Fehler beim Planen des Posts');
    }
  };

  const deletePost = async (post) => {
    try {
      // Call backend API to delete post
      const response = await fetch(`/api/businesses/${locationId}/posts/${post.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        console.log('✅ Post deleted successfully');
        
        setPosts(prev => ({
          ...prev,
          [post.status === 'suggestion' ? 'suggestions' : 
           post.status === 'scheduled' ? 'scheduled' : 
           post.status === 'published' ? 'published' : 'drafts']: 
            prev[post.status === 'suggestion' ? 'suggestions' : 
                post.status === 'scheduled' ? 'scheduled' : 
                post.status === 'published' ? 'published' : 'drafts']
              .filter(p => p.id !== post.id)
        }));
      } else {
        console.error('❌ Failed to delete post');
        alert('Fehler beim Löschen des Posts');
      }
    } catch (error) {
      console.error('❌ Error deleting post:', error);
      alert('Fehler beim Löschen des Posts');
    }
  };

  const createTemplate = (post) => {
    // Implementation for creating template from post
    console.log('Creating template from post:', post);
  };

  const handleBulkAction = () => {
    if (!bulkAction || selectedPosts.length === 0) return;

    // Block viewers from modifying content
    if (isReadOnly() && ['publish', 'delete'].includes(bulkAction)) {
      alert('Sie haben keine Berechtigung, Posts zu ändern');
      return;
    }

    switch (bulkAction) {
      case 'publish':
        selectedPosts.forEach(postId => {
          const post = getAllPosts().find(p => p.id === postId);
          if (post) publishPost(post);
        });
        break;
      case 'delete':
        // Show confirmation dialog for bulk delete
        if (selectedPosts.length > 0) {
          setDeleteConfirmDialog({ open: true, post: { id: 'bulk', count: selectedPosts.length } });
        }
        break;
      case 'schedule':
        // Open bulk schedule dialog
        break;
      default:
        break;
    }

    setSelectedPosts([]);
    setBulkAction('');
  };

  const getAllPosts = () => {
    return [...posts.suggestions, ...posts.scheduled, ...posts.published, ...posts.drafts];
  };

  const getFilteredPosts = (postList) => {
    let filtered = [...postList];

    // Apply text search
    if (searchTerm) {
      filtered = filtered.filter(post => 
        post.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.targetKeywords?.some(keyword => 
          keyword.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    // Apply filters
    switch (filter) {
      case 'today':
        filtered = filtered.filter(post =>
          post.scheduledFor && isToday(parseISO(post.scheduledFor))
        );
        break;
      case 'high-impact':
        filtered = filtered.filter(post => post.rankingImpact >= 8);
        break;
      case 'overdue':
        filtered = filtered.filter(post =>
          post.scheduledFor && isPast(parseISO(post.scheduledFor)) && post.status === 'scheduled'
        );
        break;
      case 'scheduled':
        filtered = posts.scheduled || [];
        break;
      case 'published':
        filtered = posts.published || [];
        break;
      case 'suggestions':
        filtered = posts.suggestions || [];
        break;
      default:
        break;
    }

    // Apply sorting
    switch (sortBy) {
      case 'created':
        filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      case 'impact':
        filtered.sort((a, b) => (b.rankingImpact || 0) - (a.rankingImpact || 0));
        break;
      case 'engagement':
        filtered.sort((a, b) => (b.engagementScore || 0) - (a.engagementScore || 0));
        break;
      case 'scheduled':
        filtered.sort((a, b) => {
          if (!a.scheduledFor && !b.scheduledFor) return 0;
          if (!a.scheduledFor) return 1;
          if (!b.scheduledFor) return -1;
          return new Date(a.scheduledFor) - new Date(b.scheduledFor);
        });
        break;
      default:
        break;
    }

    return filtered;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'suggestion': return amDesignColors.primary;
      case 'scheduled': return amDesignColors.warning;
      case 'published': return amDesignColors.success;
      case 'draft': return amDesignColors.text.tertiary;
      default: return amDesignColors.text.tertiary;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'suggestion': return 'KI-Vorschlag';
      case 'scheduled': return 'Geplant';
      case 'published': return 'Veröffentlicht';
      case 'draft': return 'Entwurf';
      default: return status;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = dateString instanceof Date ? dateString : parseISO(dateString);
    if (isToday(date)) return 'Heute';
    if (isTomorrow(date)) return 'Morgen';
    return format(date, 'dd.MM.yyyy', { locale: de });
  };

  const uniquePosts = posts.published.filter((post, index, self) => 
  index === self.findIndex(p => 
    p.summary.trim().toLowerCase() === post.summary.trim().toLowerCase()
  )
);

  const tabs = [
    {
      label: 'Geplant',
      count: posts.scheduled.length,
      posts: getFilteredPosts(posts.scheduled),
      color: amDesignColors.warning
    },
    {
      label: 'Veröffentlicht',
      count:uniquePosts.length,
      posts: getFilteredPosts(posts.published),
      color: amDesignColors.success
    },
    {
      label: 'Entwürfe',
      count: posts.drafts.length,
      posts: getFilteredPosts(posts.drafts),
      color: amDesignColors.text.tertiary
    }
  ];

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (!selectedBusiness) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="warning">
          Bitte wählen Sie ein Unternehmen aus, um Posts zu verwalten.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 1 }}>
      {/* Top-Level Tabs: Posts / Media */}
      <Box sx={{ mb: 2, borderBottom: `2px solid ${amDesignColors.border}` }}>
        <Box sx={{ display: 'flex', gap: 0 }}>
          <Button
            onClick={() => setTopLevelTab(0)}
            sx={{
              textTransform: 'none',
              fontSize: '0.95rem',
              fontWeight: topLevelTab === 0 ? 600 : 500,
              color: topLevelTab === 0 ? amDesignColors.primary : amDesignColors.text.secondary,
              borderBottom: topLevelTab === 0 ? `3px solid ${amDesignColors.primary}` : 'none',
              borderRadius: 0,
              paddingBottom: 1.5,
              marginBottom: '-2px'
            }}
          >
            Posts
          </Button>
          <Button
            onClick={() => setTopLevelTab(1)}
            sx={{
              textTransform: 'none',
              fontSize: '0.95rem',
              fontWeight: topLevelTab === 1 ? 600 : 500,
              color: topLevelTab === 1 ? amDesignColors.primary : amDesignColors.text.secondary,
              borderBottom: topLevelTab === 1 ? `3px solid ${amDesignColors.primary}` : 'none',
              borderRadius: 0,
              paddingBottom: 1.5,
              marginBottom: '-2px',
              marginLeft: 2
            }}
          >
            Media
          </Button>
          <Button
            onClick={() => setTopLevelTab(2)}
            sx={{
              textTransform: 'none',
              fontSize: '0.95rem',
              fontWeight: topLevelTab === 2 ? 600 : 500,
              color: topLevelTab === 2 ? amDesignColors.primary : amDesignColors.text.secondary,
              borderBottom: topLevelTab === 2 ? `3px solid ${amDesignColors.primary}` : 'none',
              borderRadius: 0,
              paddingBottom: 1.5,
              marginBottom: '-2px',
              marginLeft: 2
            }}
          >
            Bibliothek
          </Button>
        </Box>
      </Box>

      {/* Posts Tab Content */}
      {topLevelTab === 0 && (
        <>
          {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        {/* Main Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
          <Box>
            {/* Overline */}
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mb: 1,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: amDesignColors.primary,
                fontSize: '0.75rem'
              }}
            >
              Lokale SEO-Inhalte
            </Typography>

            {/* H1 */}
            <Typography
              variant="h4"
              sx={{
                mb: 0.5,
                fontWeight: 800,
                letterSpacing: '-0.025em',
                fontSize: '2rem'
              }}
            >
              Inhalte, die lokal ranken
            </Typography>

            {/* Subtitle */}
            <Typography
              variant="body2"
              sx={{
                color: amDesignColors.text.secondary
              }}
            >
              22 Beiträge · Rhythmus „alle 2 Tage" · {selectedBusiness.businessName}
            </Typography>
          </Box>

          {!isReadOnly() && (
            <Button
              variant="contained"
              startIcon={loadingWizard ? <CircularProgress size={16} color="inherit" /> : <AIIcon />}
              onClick={async () => {
                setLoadingWizard(true);
                try {
                  if (regionalKeywords.length === 0) {
                    await loadRegionalKeywords();
                  }
                  setWizardDialog({ open: true });
                } finally {
                  setLoadingWizard(false);
                }
              }}
              disabled={loadingWizard}
              size="large"
              sx={{
                bgcolor: amDesignColors.primary,
                color: 'white',
                '&:hover': { bgcolor: amDesignColors.primaryDark },
                '&:disabled': { bgcolor: '#C41E3A', opacity: 0.8 },
                fontWeight: 600,
                px: 2.5,
                py: 1.3,
                textTransform: 'none',
                fontSize: '0.95rem',
                borderRadius: '12px'
              }}
            >
              {loadingWizard ? 'Laden...' : 'Content-Wizard starten'}
            </Button>
          )}
        </Box>

       

        {/* Publication Schedule Section */}
        {posts.scheduled.length > 0 && (
          <Paper sx={{
            backgroundColor: '#1a1a1a',
            color: 'white',
            p: 3,
            borderRadius: 3,
            mb: 4
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon sx={{ fontSize: '1.2rem' }} />
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, fontSize: '1rem' }}>
                  Veröffentlichungsplan
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: amDesignColors.text.tertiary, fontSize: '0.8rem' }}>
                Nächste 5 von {posts.scheduled.length} geplanten
              </Typography>
            </Box>

            {/* Horizontal scroll for upcoming posts */}
            <Box sx={{
              display: 'flex',
              gap: 1.5,
              overflowX: 'auto',
              pb: 1,
              '&::-webkit-scrollbar': { height: '4px' },
              '&::-webkit-scrollbar-thumb': { backgroundColor: amDesignColors.text.secondary, borderRadius: '2px' }
            }}>
              {posts.scheduled.slice(0, 5).map((post, index) => {
                const scheduleDate = new Date(post.scheduledFor);
                return (
                  <Box
                    key={index}
                    sx={{
                      flex: '0 0 calc(20% - 12px)',
                      minWidth: 160,
                      p: 2,
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      borderRadius: 2,
                      border: '1px solid rgba(255,255,255,0.1)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1
                    }}
                  >
                    <Box>
                      <Typography variant="h6" sx={{ color: amDesignColors.primaryLight, fontWeight: 700, fontSize: '1.4rem', lineHeight: 1 }}>
                        {scheduleDate.getDate()}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'white', textTransform: 'uppercase', fontSize: '0.7rem', fontWeight: 600 }}>
                        {scheduleDate.toLocaleString('de-DE', { month: 'short' }).toUpperCase()}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: 'white', fontSize: '0.85rem', lineHeight: 1.4, flex: 1 }}>
                      {post.title || post.content.substring(0, 50) + '...'}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        )}

      </Box>

      {/* Tabs and Search Bar - Inline */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2 }}>
        {/* Tabs */}
        <Box sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 2,
          backgroundColor: '#ffffff',
          borderRadius: '20px',
          padding: '6px 8px',
          border: '1px solid #e0e0e0'
        }}>
          {tabs.map((tab, index) => (
            <Box
              key={index}
              onClick={() => setActiveTab(index)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                transition: 'all 0.2s',
                paddingX: 3,
                paddingY: 1.5,
                borderRadius: '16px',
                backgroundColor: activeTab === index ? '#ffffff' : 'transparent',
                boxShadow: activeTab === index ? '0 1px 2px rgba(16,16,20,.06), 0 2px 6px rgba(16,16,20,.05)' : 'none',
                '&:hover': {
                  opacity: 0.85
                }
              }}
            >
              <Typography sx={{
                fontWeight: 500,
                fontSize: '0.95rem',
                color: activeTab === index ? '#000000' : '#55555D',
                letterSpacing: '-0.3px'
              }}>
                {tab.label}
              </Typography>
              <Typography sx={{
                fontWeight: 700,
                color: activeTab === index ? amDesignColors.primary : '#55555D',
                fontSize: '0.95rem'
              }}>
                {tab.count}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Search Bar */}
        <TextField
          placeholder="Beiträge durchsuchen..."
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1, fontSize: '1.1rem' }} />
          }}
          sx={{
            minWidth: 250,
            '& .MuiOutlinedInput-root': {
              borderRadius: '6px'
            }
          }}
        />
      </Box>

      {/* Content */}
      <PostsContent
        posts={tabs[activeTab]?.posts || []}
        viewMode={viewMode}
        selectedPosts={selectedPosts}
        onSelectPost={(postId) => {
          setSelectedPosts(prev =>
            prev.includes(postId)
              ? prev.filter(id => id !== postId)
              : [...prev, postId]
          );
        }}
        onPostAction={handlePostAction}
        loading={loading}
        emptyMessage={getEmptyMessage(activeTab)}
        onGenerateClick={generateAIPosts}
        generating={generating}
        selectedBusiness={selectedBusiness}
        onOpenWizard={async () => {
          setLoadingWizard(true);
          try {
            // Lazy load regional keywords only when opening PostWizard
            if (regionalKeywords.length === 0) {
              await loadRegionalKeywords();
            }
            setWizardDialog({ open: true });
          } finally {
            setLoadingWizard(false);
          }
        }}
        loadingWizard={loadingWizard}
        publishedPostsError={activeTab === 2 ? publishedPostsError : null}
        onRefreshPublishedPosts={loadPublishedPosts}
        activeTab={activeTab}
        locationId={locationId}
        navigate={navigate}
        keywords={keywords}
        regionalKeywords={regionalKeywords}
        customAiPrompt={customAiPrompt}
        setCustomAiPrompt={setCustomAiPrompt}
        saveCustomAiPrompt={saveCustomAiPrompt}
        savingPrompt={savingPrompt}
        onOpenAiInstructionsDialog={() => setAiInstructionsDialog({ open: true })}
      />

      {/* Floating Action Button - Hidden for viewers */}
      {!isReadOnly() && (
        <Fab
          color="primary"
          aria-label="add"
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            bgcolor: '#C41E3A',
            '&:hover': { bgcolor: '#A41729' }
          }}
          onClick={() => setEditDialog({ open: true, post: null })}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Dialogs */}
      <ScheduleDialog
        open={scheduleDialog.open}
        post={scheduleDialog.post}
        onClose={() => setScheduleDialog({ open: false, post: null })}
        onSchedule={(post, date) => {
          schedulePost(post, date);
          setScheduleDialog({ open: false, post: null });
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={() => setDeleteConfirmDialog({ open: false, post: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Post löschen
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 2 }}>
            {deleteConfirmDialog.post?.id === 'bulk'
              ? `Möchten Sie wirklich ${deleteConfirmDialog.post?.count} Post(s) löschen? Diese Aktion kann nicht rückgängig gemacht werden.`
              : 'Möchten Sie diesen Post wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmDialog({ open: false, post: null })}>
            Abbrechen
          </Button>
          <Button
            onClick={async () => {
              if (deleteConfirmDialog.post?.id === 'bulk') {
                // Bulk delete
                const postsToDelete = selectedPosts.map(postId =>
                  getAllPosts().find(p => p.id === postId)
                ).filter(Boolean);
                for (const post of postsToDelete) {
                  await deletePost(post);
                }
                setSelectedPosts([]);
              } else {
                // Single delete
                await deletePost(deleteConfirmDialog.post);
              }
              setDeleteConfirmDialog({ open: false, post: null });
            }}
            color="error"
            variant="contained"
          >
            Löschen
          </Button>
        </DialogActions>
      </Dialog>

      <EditDialog
        open={editDialog.open}
        post={editDialog.post}
        onClose={() => setEditDialog({ open: false, post: null })}
        onSave={(updatedPost) => {
          if (editDialog.post) {
            // Editing existing post
            setPosts(prev => {
              const postStatus = updatedPost.status || 'draft';
              const targetArray = postStatus === 'suggestion' ? 'suggestions' : 
                                 postStatus === 'scheduled' ? 'scheduled' : 
                                 postStatus === 'published' ? 'published' : 'drafts';
              
              return {
                ...prev,
                [targetArray]: prev[targetArray].map(p => 
                  p.id === updatedPost.id ? updatedPost : p
                )
              };
            });
          } else {
            // Creating new post - save as draft
            const newPost = {
              id: Date.now().toString(),
              status: 'draft',
              createdAt: new Date().toISOString(),
              userId: localStorage.getItem('userId') || 'user', 
              ...updatedPost
            };
            
            setPosts(prev => ({
              ...prev,
              drafts: [...prev.drafts, newPost]
            }));
          }
          setEditDialog({ open: false, post: null });
        }}
        keywords={keywords}
      />

      {/* View Published Post Dialog */}
      <Dialog
        open={viewPostDialog.open}
        onClose={() => setViewPostDialog({ open: false, post: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Vollständiger Post
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {viewPostDialog.post && (
            <Box>
              <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
                {format(new Date(viewPostDialog.post.createTime || viewPostDialog.post.createdAt), 'dd.MM.yyyy HH:mm', { locale: de })}
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                {viewPostDialog.post.summary || viewPostDialog.post.content}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewPostDialog({ open: false, post: null })}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      <TemplateDialog
        open={templateDialog.open}
        templates={templates}
        onClose={() => setTemplateDialog({ open: false })}
        onSelect={(template) => {
          // Handle template selection
          setTemplateDialog({ open: false });
        }}
      />

      <AnalyticsDialog
        open={analyticsDialog.open}
        analytics={analytics}
        posts={getAllPosts()}
        keywordCoverage={keywordCoverage}
        onClose={() => setAnalyticsDialog({ open: false })}
      />

      <StandardPlanDialog
        open={standardPlanDialog.open}
        plans={standardPlans}
        onClose={() => setStandardPlanDialog({ open: false })}
        onExecute={executeStandardPlan}
        generating={generating}
      />

      <KeywordPlannerDialog
        open={keywordPlannerDialog.open}
        keywords={keywords}
        regionalKeywords={regionalKeywords}
        posts={getAllPosts()}
        onClose={() => setKeywordPlannerDialog({ open: false })}
        onUpdateKeywords={(newKeywords, newRegionalKeywords) => {
          setKeywords(newKeywords);
          setRegionalKeywords(newRegionalKeywords);
          calculateKeywordCoverage();
        }}
      />

      <PostsWizard
        open={wizardDialog.open}
        onClose={() => !wizardCompleting && setWizardDialog({ open: false })}
        selectedBusiness={selectedBusiness}
        keywords={keywords}
        regionalKeywords={regionalKeywords}
        rankingData={rankingData}
        existingPosts={posts}
        completing={wizardCompleting}
        onComplete={async (wizardResult) => {
          setWizardCompleting(true);
          try {
            console.log('🎉 Wizard completed with result:', wizardResult);
            
            // Save scheduled posts from wizard
            await savePostsToDatabase(wizardResult.scheduledPosts);
            
            // Update posts state
            setPosts(prev => ({
              ...prev,
              scheduled: [...prev.scheduled, ...wizardResult.scheduledPosts]
            }));
            
            // Update keywords if changed
            setKeywords(wizardResult.keywords);
            setRegionalKeywords(wizardResult.regionalKeywords);
            
            // Update analytics
            setAnalytics(prev => ({
              ...prev,
              totalPosts: prev.totalPosts + wizardResult.scheduledPosts.length,
              upcomingPosts: prev.upcomingPosts + wizardResult.scheduledPosts.length
            }));
            
            // Recalculate coverage
            setTimeout(calculateKeywordCoverage, 500);
            
            setWizardDialog({ open: false });
            
            // Switch to scheduled tab to show results
            setActiveTab(1);
            
          } catch (error) {
            console.error('Error completing wizard:', error);
          } finally {
            setWizardCompleting(false);
          }
        }}
      />

      {/* AI Instructions Dialog */}
      <Dialog
        open={aiInstructionsDialog.open}
        onClose={() => setAiInstructionsDialog({ open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
          Zusätzliche KI-Anweisungen
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            multiline
            rows={6}
            fullWidth
            placeholder="z.B. Verwende nie das Wort 'günstig'. Erwähne nicht die Stadt Berlin."
            helperText="Optional – wird nur für dieses Unternehmen an den globalen KI-Prompt angehängt und gilt für alle generierten Posts."
            value={customAiPrompt}
            onChange={(e) => setCustomAiPrompt(e.target.value)}
            disabled={!selectedBusiness}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setAiInstructionsDialog({ open: false })}
            sx={{ textTransform: 'none' }}
          >
            Abbrechen
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              await saveCustomAiPrompt();
              setAiInstructionsDialog({ open: false });
            }}
            disabled={savingPrompt || !selectedBusiness}
            sx={{
              bgcolor: amDesignColors.primary,
              '&:hover': { bgcolor: amDesignColors.primaryDark },
              textTransform: 'none'
            }}
          >
            {savingPrompt ? '⏳ Speichern...' : '💾 Speichern'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Notification */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={4000}
        onClose={() => setToastOpen(false)}
        message={toastMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />
        </>
      )}

      {/* Media Tab Content */}
      {topLevelTab === 1 && (
        <MediaManager locationId={locationId} />
      )}

      {/* Library Tab Content */}
      {topLevelTab === 2 && (
        <LibraryPage />
      )}
    </Container>
  );

  function getEmptyMessage(tabIndex) {
    switch (tabIndex) {
      case 0: return 'Keine KI-Vorschläge verfügbar. Starten Sie den Content-Wizard!';
      case 1: return `Keine geplanten Posts für "${selectedBusiness?.businessName || 'dieses Unternehmen'}" gefunden. ${posts.scheduled.length === 0 ? 'Erstellen Sie Posts mit dem Content-Wizard oder wählen Sie ein anderes Unternehmen.' : ''}`;
      case 2: return 'Keine Google My Business Posts gefunden. Veröffentlichen Sie Posts in Ihrem Google My Business Profil oder synchronisieren Sie Ihr Konto.';
      case 3: return 'Keine Entwürfe gespeichert.';
      default: return 'Keine Posts vorhanden.';
    }
  }
};

// 🏢 ENTERPRISE-GRADE PUBLISHED POSTS TABLE
const PublishedPostsTable = ({ posts, keywords = [], regionalKeywords = [] }) => {
  const [orderBy, setOrderBy] = useState('createTime');
  const [order, setOrder] = useState('desc');
  const [selectedPost, setSelectedPost] = useState(null);
  const [postDetailOpen, setPostDetailOpen] = useState(false);

  // 🎯 ADVANCED LOCAL SEO KEYWORD SCORE CALCULATOR
  const calculateKeywordScore = (text) => {
    if (!text) return 0;
    
    const lowerText = text.toLowerCase();
    let totalScore = 0;
    let maxPossibleScore = 0;
    
    // 📊 HAUPTKEYWORDS (Gewichtung: 60%)
    if (keywords.length > 0) {
      const primaryScore = calculateKeywordDensity(lowerText, keywords, 60);
      totalScore += primaryScore.score;
      maxPossibleScore += primaryScore.maxScore;
    }
    
    // 📍 REGIONALE KEYWORDS (Gewichtung: 40%)
    if (regionalKeywords.length > 0) {
      const regionalScore = calculateKeywordDensity(lowerText, regionalKeywords, 40);
      totalScore += regionalScore.score;
      maxPossibleScore += regionalScore.maxScore;
    }
    
    // 🏆 BONUS-FAKTOREN für lokales SEO
    const bonusScore = calculateLocalSEOBonus(lowerText);
    totalScore += bonusScore;
    
    // 🎯 Finaler Score (0-100%)
    if (maxPossibleScore === 0) return 0;
    
    const finalScore = Math.min(Math.round((totalScore / maxPossibleScore) * 100), 100);
    return finalScore;
  };
  
  // 📈 Keyword Density Berechnung mit SEO-Gewichtung
  const calculateKeywordDensity = (text, keywordList, weight) => {
    let score = 0;
    let maxScore = weight;
    
    keywordList.forEach(keyword => {
      const keywordLower = keyword.toLowerCase();
      const regex = new RegExp(`\\b${keywordLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
      const matches = text.match(regex) || [];
      
      if (matches.length > 0) {
        // 🎯 Optimale Keyword-Density: 1-3% (aber max 1 Punkt pro Keyword)
        const density = Math.min(matches.length / text.split(' ').length * 100, 3);
        const keywordScore = Math.min((density / 3) * (weight / keywordList.length), weight / keywordList.length);
        score += keywordScore;
      }
    });
    
    return { score, maxScore };
  };
  
  // 🏆 LOKALES SEO BONUS SYSTEM
  const calculateLocalSEOBonus = (text) => {
    let bonus = 0;
    
    // 📍 Lokale Begriffe (5 Punkte pro Treffer, max 15)
    const localTerms = [
      'hildesheim', 'himmelsthür', 'niedersachsen', 'lokal', 'vor ort',
      'regional', 'nachbarschaft', 'umgebung', 'nähe', 'standort'
    ];
    
    localTerms.forEach(term => {
      if (text.includes(term)) {
        bonus = Math.min(bonus + 5, 15);
      }
    });
    
    // 🎯 Action Words für lokales Business (3 Punkte pro Treffer, max 12)
    const actionWords = [
      'besuchen', 'kommen', 'termin', 'beratung', 'service', 'öffnungszeiten',
      'adresse', 'anfahrt', 'parkplatz', 'erreichbar'
    ];
    
    actionWords.forEach(word => {
      if (text.includes(word)) {
        bonus = Math.min(bonus + 3, 12);
      }
    });
    
    // 📞 Kontakt-Aufforderungen (8 Punkte pro Treffer, max 8)
    const contactCalls = [
      'rufen', 'anrufen', 'kontakt', 'telefon', 'whatsapp', 'nachricht',
      'vereinbaren', 'buchen'
    ];
    
    contactCalls.forEach(call => {
      if (text.includes(call)) {
        bonus = Math.min(bonus + 8, 8);
      }
    });
    
    return Math.min(bonus, 35); // Max 35 Bonuspunkte
  };
  
  // 💡 Keyword-Score Erklärung für Tooltip
  const getKeywordScoreExplanation = (text, score) => {
    const lowerText = text.toLowerCase();
    const explanations = [];
    
    // Haupt-Keywords prüfen
    if (keywords.length > 0) {
      const foundMain = keywords.filter(k => lowerText.includes(k.toLowerCase()));
      explanations.push(`Haupt-Keywords (60%): ${foundMain.length}/${keywords.length} gefunden`);
    }
    
    // Regionale Keywords prüfen
    if (regionalKeywords.length > 0) {
      const foundRegional = regionalKeywords.filter(k => lowerText.includes(k.toLowerCase()));
      explanations.push(`Regionale Keywords (40%): ${foundRegional.length}/${regionalKeywords.length} gefunden`);
    }
    
    // Lokale SEO Bonus
    const bonus = calculateLocalSEOBonus(lowerText);
    if (bonus > 0) {
      explanations.push(`Lokale SEO Bonus: +${bonus} Punkte`);
    }
    
    return explanations.join('\n');
  };
  
  // 🔍 Post-Detail Dialog öffnen
  const handlePostClick = (post) => {
    setSelectedPost(post);
    setPostDetailOpen(true);
  };
  
  // ❌ Post-Detail Dialog schließen
  const handleClosePostDetail = () => {
    setPostDetailOpen(false);
    setSelectedPost(null);
  };

  // Sort function
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Sort posts
  const sortedPosts = [...posts].sort((a, b) => {
    let aValue, bValue;
    
    switch (orderBy) {
      case 'createTime':
        aValue = new Date(a.createTime);
        bValue = new Date(b.createTime);
        break;
      case 'keywordScore':
        aValue = calculateKeywordScore(a.summary);
        bValue = calculateKeywordScore(b.summary);
        break;
      case 'views':
        aValue = a.analytics?.views || 0;
        bValue = b.analytics?.views || 0;
        break;
      case 'engagement':
        aValue = a.analytics?.engagement || 0;
        bValue = b.analytics?.engagement || 0;
        break;
      default:
        aValue = a[orderBy];
        bValue = b[orderBy];
    }
    
    if (order === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    }
    return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
  });

  const uniqueSortedPosts = sortedPosts.filter((post, index, self) => 
  index === self.findIndex(p => 
    p.summary.trim().toLowerCase() === post.summary.trim().toLowerCase()
  )
);

  if (posts.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="textSecondary" gutterBottom>
          Keine veröffentlichten Posts gefunden
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Ihre Google My Business Posts werden hier angezeigt.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      {/* Header Stats */}
      <Box sx={{ p: 3, borderBottom: 1, borderColor: amDesignColors.border }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary" fontWeight="bold">
                {uniqueSortedPosts.length}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Veröffentlichte Posts
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main" fontWeight="bold">
                {posts.reduce((sum, post) => sum + (post.analytics?.views || 0), 0)}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Gesamt-Aufrufe
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="warning.main" fontWeight="bold">
                {Math.round(posts.reduce((sum, post) => sum + calculateKeywordScore(post.summary), 0) / posts.length)}%
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Ø Keyword-Score
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="info.main" fontWeight="bold">
                {posts.filter(post => post.media?.length > 0).length}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Posts mit Medien
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Table */}
      <TableContainer sx={{ maxHeight: 600 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 80 }}>
                Thumbnail
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 300 }}>
                <TableSortLabel
                  active={orderBy === 'summary'}
                  direction={orderBy === 'summary' ? order : 'asc'}
                  onClick={() => handleSort('summary')}
                >
                  Content
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>
                <TableSortLabel
                  active={orderBy === 'createTime'}
                  direction={orderBy === 'createTime' ? order : 'asc'}
                  onClick={() => handleSort('createTime')}
                >
                  Datum
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>
                <TableSortLabel
                  active={orderBy === 'keywordScore'}
                  direction={orderBy === 'keywordScore' ? order : 'asc'}
                  onClick={() => handleSort('keywordScore')}
                >
                  Keyword-Score
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>
                SEO
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {uniqueSortedPosts.map((post, index) => {
              const keywordScore = calculateKeywordScore(post.summary);
              const postDate = new Date(post.createTime);
              
              return (
                <TableRow 
                  key={post.id} 
                  hover 
                  onClick={() => handlePostClick(post)}
                  sx={{ cursor: 'pointer' }}
                >
                  {/* Thumbnail */}
                  <TableCell>
                    <Avatar
                      src={post.thumbnail}
                      sx={{ 
                        width: 60, 
                        height: 60, 
                        borderRadius: 2,
                        bgcolor: amDesignColors.paper
                      }}
                      variant="rounded"
                    >
                      <PostIcon />
                    </Avatar>
                  </TableCell>

                  {/* Content */}
                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ 
                        fontWeight: 500, 
                        mb: 0.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {post.summary || 'Kein Text verfügbar'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {post.summary?.length || 0} Zeichen
                      </Typography>
                      {post.topicType && (
                        <Chip 
                          label={post.topicType} 
                          size="small" 
                          sx={{ ml: 1, fontSize: '0.65rem' }}
                          color={post.topicType === 'OFFER' ? 'secondary' : 'default'}
                        />
                      )}
                    </Box>
                  </TableCell>

                  {/* Date */}
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {format(postDate, 'dd.MM.yyyy', { locale: de })}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {format(postDate, 'HH:mm', { locale: de })} Uhr
                    </Typography>
                  </TableCell>

                  {/* Keyword Score */}
                  <TableCell>
                    <Tooltip 
                      title={
                        <Box sx={{ p: 1 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Lokaler SEO Score ({keywordScore}%)
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                            {getKeywordScoreExplanation(post.summary, keywordScore)}
                          </Typography>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="caption" color="textSecondary">
                            {keywordScore >= 70 ? '🎯 Excellent für lokales Ranking!' :
                             keywordScore >= 40 ? '⚡ Gut optimiert' :
                             '💡 Verbesserungspotential vorhanden'}
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="left"
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}>
                        <Box sx={{ width: '100%' }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={keywordScore} 
                            sx={{ 
                              height: 8, 
                              borderRadius: 4,
                              backgroundColor: 'grey.200',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: keywordScore >= 70 ? amDesignColors.success : 
                                               keywordScore >= 40 ? amDesignColors.warning : amDesignColors.error
                              }
                            }}
                          />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                            <Typography variant="caption" sx={{ 
                              fontWeight: 600,
                              color: keywordScore >= 70 ? amDesignColors.success : 
                                     keywordScore >= 40 ? amDesignColors.warning : amDesignColors.error
                            }}>
                              {keywordScore}%
                            </Typography>
                            {keywordScore >= 70 && <Typography variant="caption">🎯</Typography>}
                            {keywordScore >= 40 && keywordScore < 70 && <Typography variant="caption">⚡</Typography>}
                            {keywordScore < 40 && <Typography variant="caption">💡</Typography>}
                          </Box>
                        </Box>
                      </Box>
                    </Tooltip>
                  </TableCell>


                  {/* SEO */}
                  <TableCell>
                    <Rating 
                      value={Math.min(Math.round((post.summary?.length || 0) / 200), 5)} 
                      readOnly 
                      size="small"
                      sx={{ fontSize: '0.8rem' }}
                    />
                    <Typography variant="caption" display="block" color="textSecondary">
                      {post.media?.length > 0 ? 'Mit Bild' : 'Nur Text'}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>


      {/* 📱 GOOGLE MY BUSINESS POST DETAIL DIALOG */}
      <Dialog
        open={postDetailOpen}
        onClose={handleClosePostDetail}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '90vh'
          }
        }}
      >
        {selectedPost && (
          <>
            {/* Header */}
            <DialogTitle sx={{ 
              pb: 2, 
              borderBottom: 1, 
              borderColor: amDesignColors.border,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <PostIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    Google My Business Post
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {format(new Date(selectedPost.createTime), 'dd.MM.yyyy • HH:mm', { locale: de })} Uhr
                  </Typography>
                </Box>
              </Box>
              <IconButton onClick={handleClosePostDetail}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
              {/* Bild-Bereich (falls vorhanden) */}
              {selectedPost.thumbnail && (
                <Box sx={{ 
                  position: 'relative',
                  width: '100%',
                  height: 300,
                  bgcolor: amDesignColors.paper,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <img 
                    src={selectedPost.thumbnail}
                    alt="Post Bild"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain'
                    }}
                  />
                </Box>
              )}

              {/* Content Bereich */}
              <Box sx={{ p: 3 }}>
                {/* Post Type & Analytics */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  {selectedPost.topicType && (
                    <Chip 
                      label={selectedPost.topicType} 
                      color={selectedPost.topicType === 'OFFER' ? 'secondary' : 'default'}
                      size="small"
                    />
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 'auto' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PreviewIcon sx={{ fontSize: 16, color: amDesignColors.primary }} />
                      <Typography variant="caption">{selectedPost.analytics?.views || 0} Aufrufe</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TrendIcon sx={{ fontSize: 16, color: amDesignColors.success }} />
                      <Typography variant="caption">{selectedPost.analytics?.clicks || 0} Klicks</Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Vollständiger Text */}
                <Typography variant="body1" sx={{ 
                  lineHeight: 1.6,
                  mb: 3,
                  whiteSpace: 'pre-wrap'
                }}>
                  {selectedPost.summary || 'Kein Text verfügbar'}
                </Typography>

                {/* Call to Action (falls vorhanden) */ console.log('---selectedPost.callToAction.url',selectedPost.callToAction.url)}
                {selectedPost.callToAction && selectedPost.callToAction.url && selectedPost.callToAction.url.trim() !== ''  && selectedPost.callToAction.url !=   'https://am-beratung.de/' &&  (
  <Box sx={{ mb: 3 }}>
    <Button 
      variant="outlined" 
      href={selectedPost.callToAction.url}
      target="_blank"
      rel="noopener noreferrer"
      startIcon={<ShareIcon />}
    >
      {selectedPost.callToAction.actionType === 'LEARN_MORE' ? 'Mehr erfahren' :
       selectedPost.callToAction.actionType === 'CALL' ? 'Anrufen' :
       selectedPost.callToAction.actionType === 'BOOK' ? 'Buchen' :
       'Aktion'}
    </Button>
  </Box>
)}

                {/* SEO & Keyword Analyse */}
                <Paper sx={{ p: 2, bgcolor: amDesignColors.paper }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AnalyticsIcon sx={{ fontSize: 18 }} />
                    SEO Analyse
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          Keyword-Score
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={calculateKeywordScore(selectedPost.summary)} 
                            sx={{ 
                              flexGrow: 1,
                              height: 6, 
                              borderRadius: 3,
                              backgroundColor: 'grey.200',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: calculateKeywordScore(selectedPost.summary) >= 70 ? amDesignColors.success : 
                                               calculateKeywordScore(selectedPost.summary) >= 40 ? amDesignColors.warning : amDesignColors.error
                              }
                            }}
                          />
                          <Typography variant="caption" fontWeight={600}>
                            {calculateKeywordScore(selectedPost.summary)}%
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          Textlänge
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {selectedPost.summary?.length || 0} Zeichen
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="caption" color="textSecondary" gutterBottom display="block">
                        Gefundene Keywords
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {[...keywords, ...regionalKeywords]
                          .filter(keyword => selectedPost.summary?.toLowerCase().includes(keyword.toLowerCase()))
                          .map((keyword, index) => (
                            <Chip 
                              key={index}
                              label={keyword} 
                              size="small" 
                              color="primary"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          ))
                        }
                        {[...keywords, ...regionalKeywords]
                          .filter(keyword => selectedPost.summary?.toLowerCase().includes(keyword.toLowerCase()))
                          .length === 0 && (
                          <Typography variant="caption" color="textSecondary" fontStyle="italic">
                            Keine Keywords gefunden
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2, borderTop: 1, borderColor: amDesignColors.border }}>
              <Button onClick={handleClosePostDetail} color="primary">
                Schließen
              </Button>
              <Button 
                variant="contained" 
                startIcon={<CopyIcon />}
                onClick={() => {
                  navigator.clipboard.writeText(selectedPost.summary || '');
                }}
              >
                Text kopieren
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Paper>
  );
};

// Supporting Components
const PostsContent = ({
  posts,
  viewMode,
  selectedPosts,
  onSelectPost,
  onPostAction,
  loading,
  emptyMessage,
  onGenerateClick,
  generating,
  selectedBusiness,
  onOpenWizard,
  loadingWizard,
  publishedPostsError,
  onRefreshPublishedPosts,
  activeTab,
  locationId,
  navigate,
  keywords,
  regionalKeywords,
  customAiPrompt,
  setCustomAiPrompt,
  saveCustomAiPrompt,
  savingPrompt,
  onOpenAiInstructionsDialog
}) => {
  const { isReadOnly } = useAuth();

  // AI Settings Button for Custom Instructions (hidden for viewers)
  const aiSettingsButton = !isReadOnly() ? (
    <Box sx={{ mb: 2 }}>
      <Button
        variant="outlined"
        onClick={onOpenAiInstructionsDialog}
        disabled={!selectedBusiness}
        startIcon={<SettingsIcon />}
        sx={{
          textTransform: 'none',
          borderColor: amDesignColors.border,
          color: amDesignColors.text.secondary,
          '&:hover': {
            borderColor: amDesignColors.primary,
            color: amDesignColors.primary
          }
        }}
      >
        Zusätzliche KI-Anweisungen
      </Button>
    </Box>
  ) : null;

  if (loading) {
    return (
      <>
        {aiSettingsButton}
        <Grid container spacing={2}>
        {[...Array(6)].map((_, index) => (
          <Grid item xs={12} md={6} lg={4} key={index}>
            <Card>
              <CardContent>
                <Skeleton variant="text" width="60%" />
                <Skeleton variant="text" />
                <Skeleton variant="text" />
                <Skeleton variant="rectangular" height={60} sx={{ mt: 1 }} />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      </>
    );
  }

  // Show error for published posts if there's an error
  if (publishedPostsError && activeTab === 2) {
    return (
      <>
        {aiSettingsButton}
        <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Alert 
          severity="error" 
          sx={{ mb: 3, textAlign: 'left' }}
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              {/* Check if there's a correct location ID stored */}
              {localStorage.getItem('correct_location_id') && (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    const correctLocationId = localStorage.getItem('correct_location_id');
                    const currentPath = window.location.pathname;
                    const newPath = currentPath.replace(`/${locationId}/`, `/${correctLocationId}/`);
                    navigate(newPath, { replace: true });
                  }}
                  startIcon={<EditIcon />}
                >
                  Zur korrekten Location
                </Button>
              )}
              <Button
                color="inherit"
                size="small"
                onClick={async () => {
                  try {
                    await onRefreshPublishedPosts();
                  } catch (error) {
                    console.error('Retry failed:', error);
                  }
                }}
                startIcon={<RefreshIcon />}
              >
                Erneut versuchen
              </Button>
            </Box>
          }
        >
          <Typography variant="subtitle2" gutterBottom>
            Fehler beim Laden der Google My Business Posts
          </Typography>
          <Typography variant="body2">
            {publishedPostsError}
          </Typography>
        </Alert>
        
        <Typography variant="h6" color="textSecondary" gutterBottom>
          Mögliche Lösungen:
        </Typography>
        <Box sx={{ textAlign: 'left', maxWidth: 600, mx: 'auto' }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • Stellen Sie sicher, dass Ihr Google My Business Konto verbunden ist
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • Überprüfen Sie, ob Posts in Ihrem Google My Business Profil vorhanden sind
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • Die API-Verbindung könnte temporär unterbrochen sein
          </Typography>
          <Typography variant="body2">
            • Versuchen Sie, die Seite zu aktualisieren oder sich erneut anzumelden
          </Typography>
        </Box>
      </Paper>
      </>
    );
  }

  if (posts.length === 0) {
    return (
      <>
        {aiSettingsButton}
        <Box>
        {/* Welcome Card for Content Wizard */}
        <Card sx={{ mb: 3, background: 'linear-gradient(135deg, amDesignColors.success 0%, #45A049 100%)', color: 'white' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <AIIcon sx={{ fontSize: 80, mb: 2, opacity: 0.9 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: 'white' }}>
              Willkommen beim Content-Wizard!
            </Typography>
            <Typography variant="h6" sx={{ mb: 2, color: 'white', opacity: 0.9, maxWidth: 600, mx: 'auto' }}>
              Erstellen Sie in wenigen Schritten eine komplette Content-Kampagne für {selectedBusiness?.businessName}
            </Typography>
            
            {/* 📊 INTELLIGENTE POST-ANALYSE */}
            <Box sx={{ 
              bgcolor: 'rgba(255,255,255,0.15)', 
              borderRadius: 2, 
              p: 2, 
              mb: 3, 
              maxWidth: 500, 
              mx: 'auto' 
            }}>
              {(() => {
                const totalPosts = Object.values(posts).flat().length;
                const publishedCount = (posts.published || []).length;
                const scheduledCount = (posts.scheduled || []).length;
                
                if (totalPosts === 0) {
                  return (
                    <Typography variant="body2" sx={{ color: 'white', textAlign: 'center' }}>
                      🚀 Sie haben noch keine Posts erstellt.<br/>
                      Perfekt, um mit einer starken Content-Strategie zu beginnen!
                    </Typography>
                  );
                } else if (publishedCount > 0) {
                  return (
                    <Typography variant="body2" sx={{ color: 'white', textAlign: 'center' }}>
                      📈 Sie haben bereits <strong>{publishedCount} veröffentlichte Posts</strong>
                      {scheduledCount > 0 && ` und ${scheduledCount} geplante Posts`}.<br/>
                      Erweitern Sie Ihre erfolgreiche Content-Strategie!
                    </Typography>
                  );
                } else {
                  return (
                    <Typography variant="body2" sx={{ color: 'white', textAlign: 'center' }}>
                      ✨ Sie haben <strong>{totalPosts} Post{totalPosts !== 1 ? 's' : ''}</strong> in Arbeit.<br/>
                      Zeit, diese zu veröffentlichen und neue zu erstellen!
                    </Typography>
                  );
                }
              })()}
            </Box>
            <Button
              variant="contained"
              startIcon={loadingWizard ? <CircularProgress size={20} color="inherit" /> : <PlayIcon />}
              onClick={onOpenWizard}
              size="large"
              disabled={loadingWizard}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                '&:disabled': { bgcolor: 'rgba(255,255,255,0.2)', opacity: 0.8 },
                px: 4,
                py: 1.5,
                fontSize: '1.2rem',
                fontWeight: 600,
                border: '2px solid rgba(255,255,255,0.3)'
              }}
            >
              {loadingWizard ? 'Content-Wizard lädt...' : 'Content-Wizard starten'}
            </Button>
          </CardContent>
        </Card>

        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mb: 4 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: amDesignColors.primary, width: 60, height: 60, mx: 'auto', mb: 1 }}>
                <SettingsIcon sx={{ fontSize: 30 }} />
              </Avatar>
              <Typography variant="subtitle2">Strategie wählen</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: amDesignColors.success, width: 60, height: 60, mx: 'auto', mb: 1 }}>
                <AIIcon sx={{ fontSize: 30 }} />
              </Avatar>
              <Typography variant="subtitle2">KI generiert Posts</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: amDesignColors.warning, width: 60, height: 60, mx: 'auto', mb: 1 }}>
                <ScheduleIcon sx={{ fontSize: 30 }} />
              </Avatar>
              <Typography variant="subtitle2">Zeitplan erstellen</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: '#9c27b0', width: 60, height: 60, mx: 'auto', mb: 1, color: amDesignColors.white }}>
                <PublishIcon sx={{ fontSize: 30 }} />
              </Avatar>
              <Typography variant="subtitle2">Kampagne starten</Typography>
            </Box>
          </Box>
          
          <Typography variant="h6" color="textSecondary" sx={{ mb: 2 }}>
            {emptyMessage}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Der Wizard führt Sie durch den kompletten Prozess - von der Keyword-Auswahl bis zur fertigen Kampagne.
          </Typography>
        </Paper>
        </Box>
      </>
    );
  }

  // For all tabs - use card grid display
  if (viewMode === 'list') {
    return (
      <>
        {aiSettingsButton}
        <PostsList posts={posts} selectedPosts={selectedPosts} onSelectPost={onSelectPost} onPostAction={onPostAction} />
      </>
    );
  }

  return (
    <>
      {aiSettingsButton}
      <PostsGrid posts={posts} selectedPosts={selectedPosts} onSelectPost={onSelectPost} onPostAction={onPostAction} />
    </>
  );
};

const PostsGrid = ({ posts, selectedPosts, onSelectPost, onPostAction }) => (
  <Grid container spacing={3}>
    {posts.map(post => (
      <Grid item xs={12} md={6} lg={4} key={post.id}>
        <PostCard
          post={post}
          selected={selectedPosts.includes(post.id)}
          onSelect={() => onSelectPost(post.id)}
          onAction={onPostAction}
        />
      </Grid>
    ))}
  </Grid>
);

const PostsList = ({ posts, selectedPosts, onSelectPost, onPostAction }) => (
  <Paper>
    <List>
      {posts.map((post, index) => (
        <React.Fragment key={post.id}>
          <PostListItem
            post={post}
            selected={selectedPosts.includes(post.id)}
            onSelect={() => onSelectPost(post.id)}
            onAction={onPostAction}
          />
          {index < posts.length - 1 && <Divider />}
        </React.Fragment>
      ))}
    </List>
  </Paper>
);

const PostCard = ({ post, selected, onSelect, onAction }) => {
  const { isReadOnly } = useAuth();

  const getStatusColor = (status) => {
    switch (status) {
      case 'suggestion': return amDesignColors.primary;
      case 'scheduled': return amDesignColors.warning;
      case 'published': return amDesignColors.success;
      case 'draft': return amDesignColors.text.tertiary;
      default: return amDesignColors.text.tertiary;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'suggestion': return 'KI-Vorschlag';
      case 'scheduled': return 'Geplant';
      case 'published': return 'Veröffentlicht';
      case 'draft': return 'Entwurf';
      default: return status;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = dateString instanceof Date ? dateString : parseISO(dateString);
    if (isToday(date)) return 'Heute';
    if (isTomorrow(date)) return 'Morgen';
    return format(date, 'dd.MM.yyyy', { locale: de });
  };

  return (
    <Card
      sx={{
        height: '100%',
        border: '1px solid amDesignColors.border',
        borderRadius: 2,
        position: 'relative',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          transform: 'translateY(-2px)'
        }
      }}
    >
      <CardContent sx={{ p: 2.5, pb: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header: Status Badge and Date */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, px: 1.2, py: 0.6, backgroundColor: `${getStatusColor(post.status || (post.createTime ? 'published' : 'draft'))}15`, borderRadius: '6px' }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: getStatusColor(post.status || (post.createTime ? 'published' : 'draft'))
              }}
            />
            <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.85rem', color: getStatusColor(post.status || (post.createTime ? 'published' : 'draft')) }}>
              {getStatusLabel(post.status || (post.createTime ? 'published' : 'draft'))}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: '#999', fontSize: '0.75rem', fontWeight: 500 }}>
            {post.scheduledFor ? formatDate(post.scheduledFor) : post.createdAt ? formatDate(post.createdAt) : post.createTime ? formatDate(post.createTime) : 'No date'}
          </Typography>
        </Box>

       

        {/* Description */}
        <Typography
          variant="body2"
          sx={{
            color: amDesignColors.text.secondary,
            fontSize: '0.875rem',
            lineHeight: 1.5,
            mb: 2,
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical'
          }}
        >
          {post.content || post.summary}
        </Typography>

        {/* Tags */}
        {post.targetKeywords && post.targetKeywords.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
            {post.targetKeywords.slice(0, 3).map((keyword, index) => (
              <Chip
                key={index}
                label={keyword}
                size="small"
                sx={{
                  height: 24,
                  fontSize: '0.7rem',
                  backgroundColor: '#f5f5f5',
                  color: amDesignColors.text.secondary,
                  border: 'none'
                }}
              />
            ))}
          </Box>
        )}

        {/* Media Display for Scheduled Posts */}
        {post.mediaUrl && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5, fontWeight: 500 }}>
              📸 Medien:
            </Typography>
            {post.mediaUrl.toLowerCase().endsWith('.mp4') || post.mediaUrl.includes('video') ? (
              <Box sx={{
                width: '100%',
                height: 150,
                bgcolor: amDesignColors.paper,
                borderRadius: 1,
                overflow: 'hidden',
                mb: 1
              }}>
                <video
                  src={post.mediaUrl}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                  onError={() => console.log('Video failed to load')}
                />
              </Box>
            ) : (
              <Box sx={{
                width: '100%',
                height: 150,
                bgcolor: amDesignColors.paper,
                borderRadius: 1,
                overflow: 'hidden',
                mb: 1
              }}>
                <img
                  src={post.mediaUrl}
                  alt="Post media"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </Box>
            )}
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{
                display: 'block',
                wordBreak: 'break-all',
                fontSize: '0.7rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                bgcolor: amDesignColors.paper,
                p: 0.5,
                borderRadius: 0.5
              }}
              title={post.mediaUrl}
            >
              {post.mediaUrl.substring(post.mediaUrl.lastIndexOf('/') + 1)}
            </Typography>
          </Box>
        )}

        {/* Footer: Author and Actions */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pt: 1.5,
          borderTop: '1px solid amDesignColors.divider'
        }}>
          <Typography variant="caption" sx={{ color: '#999', fontSize: '0.75rem' }}>
            von {post.author || 'System'}
          </Typography>

          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Kopieren">
              <IconButton
                size="small"
                onClick={() => onAction('copy', post)}
                sx={{
                  color: '#999',
                  '&:hover': { color: amDesignColors.text.secondary }
                }}
              >
                <CopyIcon sx={{ fontSize: '1rem' }} />
              </IconButton>
            </Tooltip>

            {(post.status || (post.createTime ? 'published' : 'draft')) === 'published' ? (
              <Tooltip title="Vollständigen Post anzeigen">
                <IconButton
                  size="small"
                  onClick={() => onAction('view', post)}
                  sx={{
                    color: '#999',
                    '&:hover': { color: amDesignColors.text.secondary }
                  }}
                >
                  <MoreIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
              </Tooltip>
            ) : !isReadOnly() && (
              <Tooltip title="Bearbeiten">
                <IconButton
                  size="small"
                  onClick={() => onAction('edit', post)}
                  sx={{
                    color: '#999',
                    '&:hover': { color: amDesignColors.text.secondary }
                  }}
                >
                  <EditIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const PostListItem = ({ post, selected, onSelect, onAction }) => {
  const { isReadOnly } = useAuth();

  const getStatusColor = (status) => {
    switch (status) {
      case 'suggestion': return amDesignColors.primary;
      case 'scheduled': return amDesignColors.warning;
      case 'published': return amDesignColors.success;
      case 'draft': return amDesignColors.text.tertiary;
      default: return amDesignColors.text.tertiary;
    }
  };

  return (
    <ListItem
      sx={{
        bgcolor: selected ? 'action.selected' : 'transparent',
        borderLeft: selected ? `4px solid ${getStatusColor(post.status)}` : 'none'
      }}
    >
      <ListItemAvatar>
        <Checkbox checked={selected} onChange={onSelect} />
      </ListItemAvatar>
      
      <ListItemAvatar>
        <Avatar sx={{ bgcolor: getStatusColor(post.status) }}>
          {post.status === 'suggestion' ? <AIIcon /> :
           post.status === 'scheduled' ? <ScheduleIcon /> :
           post.status === 'published' ? <PublishedIcon /> : <EditIcon />}
        </Avatar>
      </ListItemAvatar>
      
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {post.postType || 'Post'}
            </Typography>
            {post.rankingImpact && (
              <Chip
                icon={<TrendIcon />}
                label={`${post.rankingImpact.toFixed(1)}`}
                size="small"
                color={post.rankingImpact >= 8 ? 'success' : 'default'}
              />
            )}
          </Box>
        }
        secondary={
          <Box>
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical'
              }}
            >
              {post.content}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="caption" color="textSecondary">
                {post.author}
              </Typography>
              {post.scheduledFor && (
                <Typography variant="caption" color="textSecondary">
                  📅 {format(parseISO(post.scheduledFor), 'dd.MM.yy', { locale: de })}
                </Typography>
              )}
              {post.mediaUrl && (
                <Chip
                  icon={<PhotoIcon />}
                  label="Mit Medien"
                  size="small"
                  variant="outlined"
                  color="primary"
                  sx={{ height: 20 }}
                />
              )}
              {post.status === 'published' && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Typography variant="caption" color="textSecondary">
                    👍 {post.likes}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    💬 {post.comments}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        }
      />
      
      <ListItemSecondaryAction>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Kopieren">
            <IconButton size="small" onClick={() => onAction('copy', post)}>
              <CopyIcon />
            </IconButton>
          </Tooltip>

          {!isReadOnly() && post.status === 'suggestion' && (
            <>
              <Tooltip title="Planen">
                <IconButton size="small" onClick={() => onAction('schedule', post)} color="primary">
                  <ScheduleIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Veröffentlichen">
                <IconButton size="small" onClick={() => onAction('publish', post)} color="success">
                  <PublishIcon />
                </IconButton>
              </Tooltip>
            </>
          )}

          {!isReadOnly() && (
            <Tooltip title="Bearbeiten">
              <IconButton size="small" onClick={() => onAction('edit', post)}>
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}

          {!isReadOnly() && (
            <Tooltip title="Löschen">
              <IconButton size="small" onClick={() => onAction('delete', post)} color="error">
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </ListItemSecondaryAction>
    </ListItem>
  );
};

// Dialog Components
const ScheduleDialog = ({ open, post, onClose, onSchedule }) => {
  const [scheduleDate, setScheduleDate] = useState(addDays(new Date(), 1));

  const handleSchedule = () => {
    if (post) {
      onSchedule(post, scheduleDate);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Post planen</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <TextField
            label="Veröffentlichungsdatum"
            type="datetime-local"
            value={format(scheduleDate, "yyyy-MM-dd'T'HH:mm")}
            onChange={(e) => setScheduleDate(new Date(e.target.value))}
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={{
              min: format(new Date(), "yyyy-MM-dd'T'HH:mm")
            }}
          />
        </Box>
        
        {post && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>Vorschau:</Typography>
            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="body2">{post.content}</Typography>
            </Paper>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button onClick={handleSchedule} variant="contained">Planen</Button>
      </DialogActions>
    </Dialog>
  );
};

const EditDialog = ({ open, post, onClose, onSave, keywords }) => {
  const [content, setContent] = useState('');
  const [postType, setPostType] = useState('');
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [mediaUrl, setMediaUrl] = useState('');
  const [mediaFile, setMediaFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (post) {
      setContent(post.content || '');
      setPostType(post.postType || '');
      setSelectedKeywords(post.targetKeywords || []);
      setMediaUrl(post.mediaUrl || '');
      console.log('📝 EditDialog opened with post:', {
        id: post.id,
        status: post.status,
        mediaUrl: post.mediaUrl,
        allFields: Object.keys(post)
      });
    } else {
      setContent('');
      setPostType('');
      setSelectedKeywords([]);
      setMediaUrl('');
    }
  }, [post]);

  const handleMediaFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setMediaUrl(e.target.result);
        setMediaFile(file);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveMedia = () => {
    setMediaUrl('');
    setMediaFile(null);
  };

  const handleSave = async () => {
    if (!content.trim()) {
      alert('Bitte geben Sie einen Post-Inhalt ein.');
      return;
    }

    let finalMediaUrl = mediaUrl;

    // If a new file was selected, upload it to ImageKit
    if (mediaFile && mediaFile.size > 0 && mediaUrl.startsWith('data:')) {
      try {
        setUploading(true);
        const formData = new FormData();
        formData.append('file', mediaFile);

        const uploadResponse = await fetch('/api/imagekit/upload', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: formData
        });

        if (!uploadResponse.ok) {
          throw new Error(`Upload failed: ${uploadResponse.statusText}`);
        }

        const result = await uploadResponse.json();
        finalMediaUrl = result.url;
        console.log('Media uploaded to ImageKit:', finalMediaUrl);
      } catch (error) {
        console.error('Error uploading media:', error);
        alert('Fehler beim Hochladen der Datei. Bitte versuchen Sie es später erneut.');
        setUploading(false);
        return;
      } finally {
        setUploading(false);
      }
    }

    const updatedPost = {
      ...(post || {}),
      content,
      postType,
      targetKeywords: selectedKeywords,
      mediaUrl: finalMediaUrl || null,
      updatedAt: new Date().toISOString()
    };
    onSave(updatedPost);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{post ? 'Post bearbeiten' : 'Neuer Post'}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <FormControl fullWidth>
            <InputLabel>Post-Typ</InputLabel>
            <Select value={postType} onChange={(e) => setPostType(e.target.value)} label="Post-Typ">
              <MenuItem value="Service">Service</MenuItem>
              <MenuItem value="Aktion">Aktion</MenuItem>
              <MenuItem value="Tipp">Tipp</MenuItem>
              <MenuItem value="Bewertung">Bewertung</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Post-Inhalt"
            multiline
            rows={8}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            fullWidth
            placeholder="Schreiben Sie Ihren Post..."
          />

          <Box>
            <Typography variant="subtitle2" gutterBottom>Keywords auswählen:</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {keywords.map((keyword) => (
                <Chip
                  key={keyword}
                  label={keyword}
                  onClick={() => {
                    setSelectedKeywords(prev =>
                      prev.includes(keyword)
                        ? prev.filter(k => k !== keyword)
                        : [...prev, keyword]
                    );
                  }}
                  color={selectedKeywords.includes(keyword) ? 'primary' : 'default'}
                  variant={selectedKeywords.includes(keyword) ? 'filled' : 'outlined'}
                />
              ))}
            </Box>
          </Box>

          {/* Media Section */}
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              📸 Medien verwalten:
            </Typography>

            {/* Current Media Display */}
            {mediaUrl && (
              <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 500, display: 'block', mb: 1 }}>
                  Aktuelles Bild/Video:
                </Typography>
                {mediaUrl.toLowerCase().endsWith('.mp4') || mediaUrl.includes('video') || mediaUrl.startsWith('data:video') ? (
                  <Box sx={{
                    width: '100%',
                    height: 180,
                    bgcolor: amDesignColors.text.primary,
                    borderRadius: 1,
                    overflow: 'hidden',
                    mb: 1
                  }}>
                    <video
                      src={mediaUrl}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                      }}
                      controls
                    />
                  </Box>
                ) : (
                  <Box sx={{
                    width: '100%',
                    height: 180,
                    bgcolor: amDesignColors.divider,
                    borderRadius: 1,
                    overflow: 'hidden',
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <img
                      src={mediaUrl}
                      alt="Post media"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </Box>
                )}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={handleRemoveMedia}
                    fullWidth
                  >
                    Medien entfernen
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    onClick={() => document.getElementById('media-file-input').click()}
                    fullWidth
                  >
                    Ersetzen
                  </Button>
                </Box>
              </Box>
            )}

            {/* Upload New Media */}
            <Box
              sx={{
                border: '2px dashed',
                borderColor: mediaFile ? 'primary.main' : amDesignColors.border,
                borderRadius: 1,
                p: 2,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s',
                '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50' },
                bgcolor: mediaUrl ? 'transparent' : amDesignColors.paper
              }}
              onClick={() => document.getElementById('media-file-input').click()}
            >
              <input
                id="media-file-input"
                type="file"
                hidden
                onChange={handleMediaFileSelect}
                accept="image/*,video/*"
                disabled={uploading}
              />
              <PhotoIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {mediaFile ? mediaFile.name : mediaUrl ? 'Neue Datei hochladen' : 'Klicken zum Hochladen'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                oder Datei hierher ziehen
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={uploading}>
          Abbrechen
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!content.trim() || uploading}
        >
          {uploading ? 'Wird hochgeladen...' : post ? 'Speichern' : 'Erstellen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const TemplateDialog = ({ open, templates, onClose, onSelect }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Post-Templates</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {templates.map((template) => (
            <Grid item xs={12} sm={6} key={template.id}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 6 }
                }}
                onClick={() => onSelect(template)}
              >
                <CardContent>
                  <Typography variant="h6" gutterBottom>{template.name}</Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Kategorie: {template.category}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {template.usage}x verwendet
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
};

const AnalyticsDialog = ({ open, analytics, posts, keywordCoverage, onClose }) => {
  const publishedPosts = posts.filter(p => p.status === 'published');
  const scheduledPosts = posts.filter(p => p.status === 'scheduled');

  // Calculate unique posts to avoid duplicates
  const uniquePosts = publishedPosts.filter((post, index, self) =>
    index === self.findIndex(p =>
      p.summary?.trim().toLowerCase() === post.summary?.trim().toLowerCase()
    )
  );

  const totalEngagement = publishedPosts.reduce((sum, post) => sum + (post.likes + post.comments + post.shares), 0);
  const avgEngagementPerPost = publishedPosts.length > 0 ? totalEngagement / publishedPosts.length : 0;
  const avgLocalSeoScore = posts.filter(p => p.localSeoScore).reduce((sum, post) => sum + post.localSeoScore, 0) / posts.filter(p => p.localSeoScore).length || 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Local SEO Analytics</DialogTitle>
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: `${amDesignColors.primary}10` }}>
              <Typography variant="h3" sx={{ fontWeight: 700, color: amDesignColors.primary }}>
                {(uniquePosts.length + scheduledPosts.length) || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                SEO Posts
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: `${amDesignColors.success}10` }}>
              <Typography variant="h3" sx={{ fontWeight: 700, color: amDesignColors.success }}>
                {keywordCoverage.total.toFixed(0)}%
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Keyword-Abdeckung
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: `${amDesignColors.warning}10` }}>
              <Typography variant="h3" sx={{ fontWeight: 700, color: amDesignColors.warning }}>
                {avgLocalSeoScore.toFixed(0)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Ø Local SEO Score
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#f3e5f515' }}>
              <Typography variant="h3" sx={{ fontWeight: 700, color: '#9c27b0' }}>
                {keywordCoverage.regional.toFixed(0)}%
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Regional Coverage
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Keyword Performance</Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>Haupt-Keywords: {keywordCoverage.primary.toFixed(0)}%</Typography>
                <Box sx={{ width: '100%', height: 8, bgcolor: amDesignColors.paper, borderRadius: 1 }}>
                  <Box sx={{ width: `${keywordCoverage.primary}%`, height: '100%', bgcolor: amDesignColors.primary, borderRadius: 1 }} />
                </Box>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>Regional-Keywords: {keywordCoverage.regional.toFixed(0)}%</Typography>
                <Box sx={{ width: '100%', height: 8, bgcolor: amDesignColors.paper, borderRadius: 1 }}>
                  <Box sx={{ width: `${keywordCoverage.regional}%`, height: '100%', bgcolor: amDesignColors.success, borderRadius: 1 }} />
                </Box>
              </Box>
              <Box>
                <Typography variant="body2" gutterBottom>Long-Tail: {keywordCoverage.longTail.toFixed(0)}%</Typography>
                <Box sx={{ width: '100%', height: 8, bgcolor: amDesignColors.paper, borderRadius: 1 }}>
                  <Box sx={{ width: `${keywordCoverage.longTail}%`, height: '100%', bgcolor: amDesignColors.warning, borderRadius: 1 }} />
                </Box>
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Top SEO Posts</Typography>
              {posts
                .filter(p => p.localSeoScore)
                .sort((a, b) => b.localSeoScore - a.localSeoScore)
                .slice(0, 5)
                .map((post, index) => (
                  <Box key={post.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, borderBottom: `1px solid ${amDesignColors.border}` }}>
                    <Box sx={{ flex: 1, mr: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {post.postType}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {post.content.substring(0, 60)}...
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="h6" sx={{ color: amDesignColors.success, fontWeight: 700 }}>
                        {post.localSeoScore.toFixed(0)}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        SEO Score
                      </Typography>
                    </Box>
                  </Box>
                ))}
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
};

// Standard Plan Dialog
const StandardPlanDialog = ({ open, plans, onClose, onExecute, generating }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PlayIcon sx={{ color: amDesignColors.warning }} />
          Standardpläne für lokale SEO-Dominanz
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {plans.map((plan) => (
            <Grid item xs={12} key={plan.id}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  border: '2px solid transparent',
                  '&:hover': { 
                    boxShadow: 6,
                    borderColor: amDesignColors.warning
                  }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: amDesignColors.warning }}>
                        {plan.name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        {plan.description}
                      </Typography>
                    </Box>
                    <Chip 
                      label={`${plan.postsCount} Posts`}
                      sx={{ bgcolor: amDesignColors.warning, color: 'white', fontWeight: 600 }}
                    />
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="caption">{plan.duration} Tage</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="caption">{plan.schedule === 'workdays' ? 'Werktags' : 'Alle 2 Tage'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TrendIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="caption">{plan.keywordFocus} Keywords</Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="textSecondary" gutterBottom sx={{ display: 'block' }}>
                      Kategorien:
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {plan.categories.map((category, index) => (
                        <Chip key={index} label={category} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  </Box>
                  
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => onExecute(plan)}
                    disabled={generating}
                    startIcon={generating ? <CircularProgress size={20} /> : <PlayIcon />}
                    sx={{ 
                      bgcolor: amDesignColors.warning,
                      '&:hover': { bgcolor: '#F57C00' },
                      fontWeight: 600
                    }}
                  >
                    {generating ? 'Erstelle Plan...' : 'Plan ausführen'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
};

// Keyword Planner Dialog
const KeywordPlannerDialog = ({ open, keywords, regionalKeywords, posts, onClose, onUpdateKeywords }) => {
  const [newKeyword, setNewKeyword] = useState('');
  const [newRegionalKeyword, setNewRegionalKeyword] = useState('');
  const [localKeywords, setLocalKeywords] = useState(keywords);
  const [localRegionalKeywords, setLocalRegionalKeywords] = useState(regionalKeywords);

  useEffect(() => {
    if (open) {
      setLocalKeywords(keywords);
      setLocalRegionalKeywords(regionalKeywords);
    }
  }, [open]);

  const addKeyword = (type) => {
    if (type === 'primary' && newKeyword.trim()) {
      setLocalKeywords(prev => [...prev, newKeyword.trim()]);
      setNewKeyword('');
    } else if (type === 'regional' && newRegionalKeyword.trim()) {
      setLocalRegionalKeywords(prev => [...prev, newRegionalKeyword.trim()]);
      setNewRegionalKeyword('');
    }
  };

  const removeKeyword = (keyword, type) => {
    if (type === 'primary') {
      setLocalKeywords(prev => prev.filter(k => k !== keyword));
    } else {
      setLocalRegionalKeywords(prev => prev.filter(k => k !== keyword));
    }
  };

  const handleSave = () => {
    onUpdateKeywords(localKeywords, localRegionalKeywords);
    onClose();
  };

  const getKeywordUsage = (keyword) => {
    return posts.filter(post => 
      post.targetKeywords && post.targetKeywords.includes(keyword)
    ).length;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SearchIcon sx={{ color: amDesignColors.primary }} />
          Keyword-Planer
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>Haupt-Keywords</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                size="small"
                placeholder="Neues Keyword hinzufügen"
                value={newKeyword}
                onChange={(e) => setNewKeyword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addKeyword('primary')}
                fullWidth
              />
              <Button 
                variant="contained" 
                onClick={() => addKeyword('primary')}
                disabled={!newKeyword.trim()}
              >
                +
              </Button>
            </Box>
            <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
              {localKeywords.map((keyword, index) => (
                <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, borderBottom: '1px solid #eee' }}>
                  <Box>
                    <Typography variant="body2">{keyword}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {getKeywordUsage(keyword)} Posts verwenden dieses Keyword
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={() => removeKeyword(keyword, 'primary')} color="error">
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>Regional-Keywords</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                size="small"
                placeholder="Neue Region/Stadt hinzufügen"
                value={newRegionalKeyword}
                onChange={(e) => setNewRegionalKeyword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addKeyword('regional')}
                fullWidth
              />
              <Button 
                variant="contained" 
                onClick={() => addKeyword('regional')}
                disabled={!newRegionalKeyword.trim()}
                sx={{ bgcolor: amDesignColors.success }}
              >
                +
              </Button>
            </Box>
            <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
              {localRegionalKeywords.map((keyword, index) => (
                <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, borderBottom: '1px solid #eee' }}>
                  <Box>
                    <Typography variant="body2">{keyword}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {getKeywordUsage(keyword)} Posts verwenden dieses Keyword
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={() => removeKeyword(keyword, 'regional')} color="error">
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button onClick={handleSave} variant="contained">Speichern</Button>
      </DialogActions>
    </Dialog>
  );
};


export default PostsManager;
